import { CONSTANTS } from '../game.js';

export class StretchGaugeManager {
    constructor(scene) {
        this.scene = scene;
        
        // ゲージ要素
        this.gaugeBackground = null;
        this.gaugeBar = null;
        this.passMarker = null;
        this.goodMarker = null;
        this.greatMarker = null;
        this.perfectMarker = null;
        
        // ゲージのサイズと位置
        this.gaugeBaseWidth = 300; // 基本幅
        this.gaugeHeight = 20;
        this.gaugeX = 0;
        this.gaugeY = 0;
    }
    
    /**
     * ゴム紐の伸び率ゲージを作成
     * @param {number} initialMaxLength - 初期の最大長さ
     */
    createStretchGauge(initialMaxLength) {
        const gameWidth = this.scene.game.config.width;
        const gameHeight = this.scene.game.config.height;
        
        // ゲージの位置を設定
        this.gaugeY = gameHeight - this.gaugeHeight - 20; // 画面下部から20px上
        
        // 初期の最大長さに応じたゲージ幅を計算
        this.updateGaugeWidth(initialMaxLength);
        
        // 背景（黒の長方形）
        this.createGaugeElements(initialMaxLength);
    }
    
    /**
     * ゲージの幅を更新
     * @param {number} maxLength - ゴム紐の最大長さ
     */
    updateGaugeWidth(maxLength) {
        const gaugeWidth = maxLength;
        
        // ゲージの中央位置を計算
        const gameWidth = this.scene.game.config.width;
        this.gaugeX = gameWidth / 2 - gaugeWidth / 2;
        
        return gaugeWidth;
    }
    
    /**
     * ゲージの要素を作成
     * @param {number} maxLength - ゴム紐の最大長さ
     */
    createGaugeElements(maxLength) {
        // 既存のゲージ要素をクリーンアップ
        this.cleanupGaugeElements();
        
        // 現在のゲージ幅を取得
        const gaugeWidth = this.updateGaugeWidth(maxLength || CONSTANTS.RUBBER_BAND.NATURAL_LENGTH * 2);
        
        // 背景（黒の長方形）
        this.gaugeBackground = this.scene.add.rectangle(
            this.gaugeX,
            this.gaugeY,
            gaugeWidth,
            this.gaugeHeight,
            0x222222
        );
        this.gaugeBackground.setOrigin(0, 0); // 左上を原点に
        this.gaugeBackground.setDepth(25);
        
        // 色の区分を示す背景マーカー（FAILは除外）
        // PASS区分（水色）
        const passWidth = (CONSTANTS.STRETCH_EVALUATION.PASS.max - CONSTANTS.STRETCH_EVALUATION.PASS.min) * gaugeWidth;
        this.passMarker = this.scene.add.rectangle(
            this.gaugeX,
            this.gaugeY,
            passWidth,
            this.gaugeHeight,
            parseInt(CONSTANTS.EVALUATION.SETTINGS.PASS.color.replace('#', '0x'), 16)
        );
        this.passMarker.setOrigin(0, 0);
        this.passMarker.setDepth(25);
        this.passMarker.setAlpha(0.6);
        
        // GOOD区分（緑）
        const goodWidth = (CONSTANTS.STRETCH_EVALUATION.GOOD.max - CONSTANTS.STRETCH_EVALUATION.GOOD.min) * gaugeWidth;
        this.goodMarker = this.scene.add.rectangle(
            this.gaugeX + passWidth,
            this.gaugeY,
            goodWidth,
            this.gaugeHeight,
            parseInt(CONSTANTS.EVALUATION.SETTINGS.GOOD.color.replace('#', '0x'), 16)
        );
        this.goodMarker.setOrigin(0, 0);
        this.goodMarker.setDepth(25);
        this.goodMarker.setAlpha(0.6);
        
        // GREAT区分（赤）
        const greatWidth = (CONSTANTS.STRETCH_EVALUATION.GREAT.max - CONSTANTS.STRETCH_EVALUATION.GREAT.min) * gaugeWidth;
        this.greatMarker = this.scene.add.rectangle(
            this.gaugeX + passWidth + goodWidth,
            this.gaugeY,
            greatWidth,
            this.gaugeHeight,
            parseInt(CONSTANTS.EVALUATION.SETTINGS.GREAT.color.replace('#', '0x'), 16)
        );
        this.greatMarker.setOrigin(0, 0);
        this.greatMarker.setDepth(25);
        this.greatMarker.setAlpha(0.6);
        
        // PERFECT区分（紫）
        const perfectWidth = (1.0 - CONSTANTS.STRETCH_EVALUATION.PERFECT.min) * gaugeWidth;
        this.perfectMarker = this.scene.add.rectangle(
            this.gaugeX + passWidth + goodWidth + greatWidth,
            this.gaugeY,
            perfectWidth,
            this.gaugeHeight,
            parseInt(CONSTANTS.EVALUATION.SETTINGS.PERFECT.color.replace('#', '0x'), 16)
        );
        this.perfectMarker.setOrigin(0, 0);
        this.perfectMarker.setDepth(25);
        this.perfectMarker.setAlpha(0.6);
        
        // ゲージバー（白色で半透明）
        this.gaugeBar = this.scene.add.rectangle(
            this.gaugeX + 2, // 背景より少し右に
            this.gaugeY + 2, // 背景より少し下に
            0, // 初期幅は0
            this.gaugeHeight - 4, // 背景より少し小さく
            0xffffff // 白色
        );
        this.gaugeBar.setOrigin(0, 0); // 左上を原点に
        this.gaugeBar.setDepth(26);
        this.gaugeBar.setAlpha(0.7); // 半透明効果
    }
    
    /**
     * ゲージ要素のクリーンアップ
     */
    cleanupGaugeElements() {
        if (this.gaugeBackground) {
            this.gaugeBackground.destroy();
            this.gaugeBackground = null;
        }
        
        if (this.gaugeBar) {
            this.gaugeBar.destroy();
            this.gaugeBar = null;
        }
        
        if (this.passMarker) {
            this.passMarker.destroy();
            this.passMarker = null;
        }
        
        if (this.goodMarker) {
            this.goodMarker.destroy();
            this.goodMarker = null;
        }
        
        if (this.greatMarker) {
            this.greatMarker.destroy();
            this.greatMarker = null;
        }
        
        if (this.perfectMarker) {
            this.perfectMarker.destroy();
            this.perfectMarker = null;
        }
    }
    
    /**
     * ゴム紐の伸び率ゲージを更新
     * @param {number} currentLength - 現在のゴム紐の長さ
     * @param {number} maxLength - ゴム紐の最大長さ
     * @param {number} naturalLength - ゴム紐の自然長
     */
    updateStretchGauge(currentLength, maxLength, naturalLength) {
        if (!this.gaugeBar || !this.gaugeBackground) return;
        
        // 自然長を基準とした伸び率の計算
        let stretchRatio;
        
        if (currentLength <= naturalLength) {
            // 自然長以下の場合は現在の長さ / 自然長 の比率で表示
            stretchRatio = currentLength / naturalLength;
        } else {
            // 自然長を超えた場合の伸び率（0～1の範囲、1で切れる）
            stretchRatio = Math.min((currentLength - naturalLength) / naturalLength, 1);
        }
        
        const newWidth = (currentLength > maxLength) ? maxLength : currentLength;
        
        // ゲージの幅を設定
        this.gaugeBar.width = newWidth;
        
        // 色を固定（明るい色）
        this.gaugeBar.setFillStyle(0xffffff); // 白色
        
        // 半透明効果を適用（背景色が透けて見える）
        this.gaugeBar.setAlpha(0.7);
        
        // デバッグ情報をコンソールに出力
        // console.log(`StretchGauge: ratio=${stretchRatio.toFixed(2)}, width=${newWidth.toFixed(2)}`);
    }
    
    /**
     * ゴム紐の最大長さが変更された時にゲージを更新
     * @param {number} newMaxLength - 新しい最大長さ
     */
    updateMaxLength(newMaxLength) {
        if (!this.gaugeBackground) return;
        
        // 新しい最大長さに応じてゲージの幅を更新
        const newWidth = this.updateGaugeWidth(newMaxLength);
        
        // ゲージ要素を再作成
        this.createGaugeElements(newMaxLength);
        
        // 視覚的フィードバック（ゲージが一瞬明るく点滅）
        this.passMarker.setAlpha(0.9);
        this.goodMarker.setAlpha(0.9);
        this.greatMarker.setAlpha(0.9);
        this.perfectMarker.setAlpha(0.9);
        
        // アニメーションで元に戻す
        this.scene.tweens.add({
            targets: [this.passMarker, this.goodMarker, this.greatMarker, this.perfectMarker],
            alpha: 0.6,
            duration: 500,
            ease: 'Sine.easeOut'
        });
        
        console.log(`ゲージの最大長さを更新: ${newMaxLength}, 新しい幅: ${newWidth}`);
    }
    
    /**
     * リソースのクリーンアップ
     */
    cleanup() {
        this.cleanupGaugeElements();
    }
}
